import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { SelectCore } from '#/modules/commonComponents/form/select';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import {
  formatUnitQtyFloatingLabel,
  useMaterialUnitOptions,
} from '#/modules/imsMgmt/helper/materialUnit';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { LINE_REMARK_TEXTAREA_CLASS } from '#/modules/imsMgmt/receipts/shared/lineRemarkTextareaClass';
import {
  mapInvStockRowToIssueLine,
  maxIssueQtyInUnit,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/formSerialize';
import {
  ISSUE_LINES_TIP,
} from '#/modules/imsMgmt/receipts/stockIssue/schemas/stockIssueSchema';
import InvStockPickerDialog from '#/modules/imsMgmt/receipts/stockTransfer/components/InvStockPickerDialog';
import { clampQtyToAvailable } from '#/utils/quantityBounds';

/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
const ISSUE_LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(0,7.5rem)_minmax(10rem,1fr)_minmax(0,9rem)_minmax(0,8rem)_minmax(13rem,14rem)_minmax(8rem,1fr)_2.5rem]';
/* eslint-enable max-len */

const ISSUE_LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[62rem]',
);

const PANEL_MAX_H = 'max-h-[min(32rem,calc(100vh-14rem))]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  ISSUE_LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-start',
  ISSUE_LINE_GRID_COLS,
);

const FIELD_CELL_CLASS = 'min-w-0';

const MOBILE_FIELD_LABEL_CLASS = cn(
  'mb-1 text-xs text-gray-500',
  '@3xl/form-section:hidden',
);

/** 唯讀短欄：桌面橫列與同列 Input／Select 垂直居中 */
const READONLY_LINE_CELL_CLASS = cn(
  FIELD_CELL_CLASS,
  '@3xl/form-section:self-center',
);

const IssueLineRow = ({
  index,
  control,
  register,
  errors,
  line,
  onRemove,
  setValue,
}) => {
  const availableBase = Number(line.availableQty) || 0;
  const warehouseLabel = line.warehouseLabel || '-';
  const mtrId = String(line.mtrId ?? '').trim();
  const qtyId = `lines.${index}.qty`;
  const unitIdField = `lines.${index}.unitId`;
  const remarkId = `lines.${index}.remark`;
  const initialUnitOptions = Array.isArray(line.unitOptions) && line.unitOptions.length > 0
    ? line.unitOptions
    : null;

  const {
    options: unitOptions,
    selectOptions: unitSelectOptions,
    defaultUnitId,
    isLoading: unitOptionsLoading,
  } = useMaterialUnitOptions({
    mtrId,
    initialOptions: initialUnitOptions,
    enabled: Boolean(mtrId),
  });

  const unitDisabled = !mtrId || unitOptionsLoading;
  const unitError = getNestedError(errors, unitIdField);

  const { field: unitField } = useController({
    name: unitIdField,
    control,
    defaultValue: '',
  });

  const resolvedUnitOptions = (
    Array.isArray(unitOptions) && unitOptions.length > 0
      ? unitOptions
      : (line.unitOptions ?? [])
  );
  const activeUnitId = String(unitField.value || line.unitId || '').trim()
    || defaultUnitId
    || '';
  const qtyMax = maxIssueQtyInUnit(availableBase, resolvedUnitOptions, activeUnitId);
  const qtyClampMax = Math.max(qtyMax, 1);
  const qtyFloatingLabel = formatUnitQtyFloatingLabel({
    availableBase,
    qtyMax,
    tone: 'issue',
  });
  const remarkError = getNestedError(errors, remarkId);

  useEffect(() => {
    if (!mtrId || !defaultUnitId || unitOptionsLoading) {
      return;
    }
    if (String(line.unitId ?? '').trim()) {
      return;
    }
    setValue(unitIdField, defaultUnitId, {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [
    mtrId,
    defaultUnitId,
    unitOptionsLoading,
    line.unitId,
    unitIdField,
    setValue,
  ]);

  function handleUnitChange(nextUnitId) {
    unitField.onChange(nextUnitId);
    const max = maxIssueQtyInUnit(availableBase, resolvedUnitOptions, nextUnitId);
    const clamped = clampQtyToAvailable(line.qty, Math.max(max, 1));
    const nextQty = max > 0 ? Math.min(clamped, max) : clamped;
    setValue(qtyId, nextQty, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  return (
    <li className={LINE_ROW_CLASS}>
      <div className={READONLY_LINE_CELL_CLASS}>
        <p className="@3xl/form-section:hidden text-xs text-gray-500">庫存編號</p>
        <p className="font-mono text-sm text-gray-800">{line.stockNo || '-'}</p>
      </div>
      <div className={READONLY_LINE_CELL_CLASS}>
        <p className="@3xl/form-section:hidden text-xs text-gray-500">物料</p>
        <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      </div>
      <div className={READONLY_LINE_CELL_CLASS}>
        <p className="@3xl/form-section:hidden text-xs text-gray-500">倉別／儲位</p>
        <p className="truncate text-sm text-gray-800" title={warehouseLabel}>
          {warehouseLabel}
        </p>
      </div>
      <div className={FIELD_CELL_CLASS}>
        <ListFieldMobileLabel hasError={Boolean(unitError)}>
          單位
        </ListFieldMobileLabel>
        <SelectCore
          id={unitIdField}
          options={unitSelectOptions}
          value={unitField.value}
          onChange={handleUnitChange}
          onBlur={unitField.onBlur}
          isDisabled={unitDisabled}
          noOptionsMessage={
            !mtrId ? '請先選擇庫存' : '此物料尚無可選單位'
          }
          placeholder="單位"
          errors={errors}
          hideErrorMessage
          aria-label={`單位 ${index + 1}`}
          aria-invalid={unitError ? true : undefined}
          title={unitError?.message || undefined}
          className="w-full"
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <FloatingInput
          id={qtyId}
          label={qtyFloatingLabel}
          type="number"
          min={1}
          max={qtyMax > 0 ? qtyMax : undefined}
          step={1}
          register={(name) => register(name, {
            onBlur: (event) => {
              let next = clampQtyToAvailable(event.target.value, qtyClampMax);
              next = Math.floor(Number(next)) || 1;
              if (qtyMax > 0) {
                next = Math.min(next, qtyMax);
              }
              setValue(name, next, {
                shouldDirty: true,
                shouldValidate: true,
              });
            },
          })}
          errors={errors}
          suppressInlineError
          containerClassName="w-full min-w-[13rem]"
        />
      </div>
      <div className={FIELD_CELL_CLASS}>
        <p className={MOBILE_FIELD_LABEL_CLASS}>行備註</p>
        <Textarea
          id={remarkId}
          rows={3}
          aria-label={`行備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className={LINE_REMARK_TEXTAREA_CLASS}
          {...register(remarkId)}
        />
      </div>
      <div className="flex min-w-0 justify-end @3xl/form-section:pt-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除明細 ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" />
        </Button>
      </div>
    </li>
  );
};

IssueLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

const StockIssueLinesEditor = ({
  control,
  register,
  errors,
  getValues,
  setValue,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });

  const linesForRender = useMemo(() => (
    (fields ?? []).map((field, index) => {
      const watched = watchedLines?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    })
  ), [fields, watchedLines]);

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.invStockId)
      .map((line) => ({
        id: line.invStockId,
        stockNo: line.stockNo,
        mtr: {
          id: line.mtrId,
          mtrNo: line.mtrNo,
          name: line.mtrName,
        },
        baseQty: line.availableQty,
      })),
    [linesForRender],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const existingById = new Map(
      (getValues('lines') || [])
        .filter((line) => line.invStockId)
        .map((line) => [line.invStockId, line]),
    );

    const nextLines = (selectedRows || [])
      .map((row) => {
        const id = row?.id;
        if (!id) {
          return null;
        }
        const kept = existingById.get(id);
        if (kept) {
          return kept;
        }
        return mapInvStockRowToIssueLine(row);
      })
      .filter(Boolean);

    replace(nextLines);
  }

  return (
    <section className="@container/form-section">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-2">
        <h2 className="form-block__title mb-0! mr-2">出庫明細</h2>
        <Button
          type="button"
          size="sm"
          onClick={handleOpenPicker}
        >
          選擇庫存
        </Button>
      </div>

      <p className="mb-3 text-xs text-gray-500">{ISSUE_LINES_TIP}</p>

      {errors?.lines?.message ? (
        <p className="mb-3 text-sm text-red-500">{errors.lines.message}</p>
      ) : null}

      <div className={cn(PANEL_MAX_H, 'overflow-y-auto')}>
        <div className="overflow-x-auto">
          {linesForRender.length === 0 ? (
            <p className="px-1 py-8 text-center text-sm text-gray-400">
              尚未選擇任何庫存
            </p>
          ) : (
            <div className={ISSUE_LINES_MIN_W}>
              <div className={LINE_HEADER_CLASS} aria-hidden>
                <span className="min-w-0">庫存編號</span>
                <span className="min-w-0">物料</span>
                <span className="min-w-0">倉／儲位</span>
                <span className="min-w-0">單位</span>
                <span className="min-w-0">數量</span>
                <span className="min-w-0">行備註</span>
                <span className="sr-only min-w-0">操作</span>
              </div>
              <ul className="m-0 list-none divide-y divide-gray-200 p-0">
                {linesForRender.map((line, index) => (
                  <IssueLineRow
                    key={line.id}
                    index={index}
                    control={control}
                    register={register}
                    errors={errors}
                    line={line}
                    onRemove={remove}
                    setValue={setValue}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <InvStockPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        title="請選擇欲出庫的庫存"
        description="多選後確認；同單不可重複同一庫存。"
      />
    </section>
  );
};

StockIssueLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

export default StockIssueLinesEditor;
